import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Form,
  Input,
  Button,
  Card,
  message,
  Space,
  Typography,
  Row,
  Col,
  Divider,
} from "antd";
import {
  SaveOutlined,
  RollbackOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import api from "../../../../axiosConfig";
import ModuloAcceso from "../components/ModuloAcceso";

const { Title, Text } = Typography;

const EditarRol = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const isEditing = Boolean(id);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const modulosResponse = await api.get("/roles/modulos");
        const modulosData = modulosResponse.data || [];

        console.log("EditarRol - modulosData:", modulosData);

        if (isEditing) {
          const rolResponse = await api.get(`/roles/form-data/${id}`);
          const rolData = rolResponse.data;

          console.log("EditarRol - rolData:", rolData);

          // Mapear los módulos con sus configuraciones
          const modulosConfigurados = modulosData.map((modulo) => {
            const moduloConfig = rolData.modulos.find(
              (m) => m.modulo_id === modulo.id
            );
            return {
              ...modulo,
              acceso: moduloConfig?.acceso || "sin_acceso",
              permisos: moduloConfig?.permisosSeleccionados || [],
              permisosCatalogo: modulo.permisosCatalogo || [], // Agregar el catálogo de permisos
            };
          });

          form.setFieldsValue({
            nombre: rolData.nombre_rol,
            descripcion: rolData.descripcion || "",
            modulos: modulosConfigurados,
          });
        } else {
          // Para nuevo rol, inicializar todos los módulos como sin acceso
          const modulosIniciales = modulosData.map((modulo) => ({
            ...modulo,
            acceso: "sin_acceso",
            permisos: [],
            permisosCatalogo: modulo.permisosCatalogo || [], // Agregar el catálogo de permisos
          }));

          form.setFieldsValue({
            modulos: modulosIniciales,
          });
        }
      } catch (error) {
        console.error("Error al cargar los datos:", error);
        message.error("Error al cargar los datos");
        navigate("/modules/roles");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, form, navigate, isEditing]);

  const handleSubmit = async (values) => {
    try {
      const formData = {
        nombre_rol: values.nombre,
        descripcion: values.descripcion,
        modulos: values.modulos.map((modulo) => ({
          modulo_id: modulo.id,
          acceso: modulo.acceso,
          permisosSeleccionados: modulo.permisos || [],
        })),
      };

      if (isEditing) {
        await api.post(`/roles/save/${id}`, formData);
        message.success("Rol actualizado exitosamente");
      } else {
        await api.post("/roles/save/new", formData);
        message.success("Rol creado exitosamente");
      }
      navigate("/modules/roles");
    } catch (error) {
      console.error("Error al guardar:", error);
      message.error(error.response?.data?.message || "Error al guardar el rol");
    }
  };

  if (loading) {
    return <div>Cargando...</div>;
  }

  return (
    <div style={{ padding: "24px" }}>
      <Card
        style={{
          borderRadius: 8,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 24,
          }}
        >
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/modules/roles")}
              size="large"
            >
              Regresar
            </Button>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                marginLeft: 16,
              }}
            >
              <Title level={3} style={{ margin: 0, color: "#1890ff" }}>
                {isEditing ? "Editar Rol" : "Nuevo Rol"}
              </Title>
              <Text type="secondary" style={{ fontSize: "14px" }}>
                {isEditing
                  ? "Modifica los permisos y accesos del rol seleccionado"
                  : "Crea un nuevo rol y configura sus permisos de acceso"}
              </Text>
            </div>
          </Space>
        </div>

        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="nombre"
                label={<Text strong>Nombre del Rol</Text>}
                rules={[
                  {
                    required: true,
                    message: "Por favor ingrese el nombre del rol",
                  },
                ]}
              >
                <Input size="large" placeholder="Ingrese el nombre del rol" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="descripcion"
                label={<Text strong>Descripción</Text>}
              >
                <Input.TextArea
                  size="large"
                  placeholder="Ingrese una descripción del rol"
                  autoSize={{ minRows: 1, maxRows: 1 }}
                  style={{ resize: "none" }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">
            <Title level={4} style={{ margin: 0, color: "#1890ff" }}>
              Módulos y Permisos
            </Title>
          </Divider>

          <Form.List name="modulos">
            {(fields) => (
              <Row gutter={[16, 16]}>
                {fields.map(({ key, name }) => (
                  <Col xs={24} sm={24} md={12} lg={8} key={key}>
                    <Form.Item name={[name]} noStyle>
                      <ModuloAcceso
                        modulo={form.getFieldValue(["modulos", name])}
                        onChange={(value) => {
                          const currentModulos = form.getFieldValue("modulos");
                          currentModulos[name] = value;
                          form.setFieldsValue({ modulos: currentModulos });
                        }}
                      />
                    </Form.Item>
                  </Col>
                ))}
              </Row>
            )}
          </Form.List>

          <Divider />

          <Form.Item>
            <Space size="middle">
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                size="large"
              >
                {isEditing ? "Guardar Cambios" : "Crear Rol"}
              </Button>
              <Button
                onClick={() => navigate("/modules/roles")}
                icon={<RollbackOutlined />}
                size="large"
              >
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default EditarRol;
